import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ListPageShell } from "../../components/shell/ListPageShell";
import { EmptyState, PillSegmentSwitcher } from "../../components/ui";
import { useAuth } from "../../stores/auth";
import { SellerKycVerificationPanel } from "./SellerKycVerificationPanel";
import { FlexxiKycVerificationPanel } from "../FlexxiVerifications";

type VerificationTab = "seller" | "flexxi";

export function CommerceVerifications() {
  const can = useAuth((s) => s.can);
  const moduleEnabled = useAuth((s) => s.moduleEnabled);
  const [searchParams, setSearchParams] = useSearchParams();

  const showSeller = moduleEnabled("marketplace") && can("marketplace.verify_kyc");
  const showFlexxi = moduleEnabled("instalments") && can("flexxi.verify_kyc");

  const tabOptions = useMemo(() => {
    const opts: { value: VerificationTab; label: string }[] = [];
    if (showSeller) opts.push({ value: "seller", label: "Seller KYC" });
    if (showFlexxi) opts.push({ value: "flexxi", label: "FlexxiPlan KYC" });
    return opts;
  }, [showSeller, showFlexxi]);

  const requestedTab = searchParams.get("tab") as VerificationTab | null;
  const activeTab: VerificationTab | null =
    requestedTab && tabOptions.some((o) => o.value === requestedTab)
      ? requestedTab
      : (tabOptions[0]?.value ?? null);

  useEffect(() => {
    if (!activeTab) return;
    if (requestedTab === activeTab) return;
    setSearchParams({ tab: activeTab }, { replace: true });
  }, [activeTab, requestedTab, setSearchParams]);

  if (tabOptions.length === 0) {
    return (
      <ListPageShell>
        <EmptyState title="Verification unavailable" hint="You do not have access to verification queues." />
      </ListPageShell>
    );
  }

  return (
    <ListPageShell>
      {tabOptions.length > 1 ? (
        <div className="shrink-0">
          <PillSegmentSwitcher
            options={tabOptions}
            value={activeTab!}
            onChange={(v) => setSearchParams({ tab: v }, { replace: true })}
          />
        </div>
      ) : null}
      {activeTab === "seller" ? <SellerKycVerificationPanel /> : null}
      {activeTab === "flexxi" ? <FlexxiKycVerificationPanel /> : null}
    </ListPageShell>
  );
}
